import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
} from '@mui/material';
import BusyOverlay from '../commonComponents/BusyOverlay';
import { formatInputValue, formatValue } from './positionSizingFormat';
import { outputHeatmapClassName } from './outputHeatmapClasses';
import {
    compactPanelPaddingSx,
    compactSelectSx,
} from './tableStyles';
import CompactPanelHeader from '../commonComponents/CompactPanelHeader';
import { cardClasses } from '../../styles/tailwindClasses';
import { analyticsPanelSx } from '../../styles/analyticsMsiStyles';
import { attributeOptionKey } from '../../utils/positionSizingAttributeContract';
import { buildPositionSizingOutputHeatmap } from '../../utils/positionSizingOutputHeatmap';
import DataTable from '../data_table/DataTable';
import EditableNumberCell from '../data_table/EditableNumberCell';
import { sortByNumber, sortByText } from '../data_table/sorting';

const EMPTY_ROW_COUNT = 20;
const PINNED_COLUMNS = Object.freeze(['category']);
const EDITABLE_FIELDS = new Set(['scaler', 'target_vol_pct', 'target_vol_dollar']);
const OUTPUT_FIELDS = Object.freeze([
    'target_capital',
    'target_gmv',
    'pf_vol_dollar',
    'idio_vol_dollar',
    'factor_vol_dollar',
    'idio_var_pct',
    'edge_dollar',
    'gross_beta_exp',
]);
// The settings a user enters, as in the sizing table.
const USER_INPUT_CELL_CLASS_NAME = 'bg-ui-info-bg';
const PLACEHOLDER_ROW_CLASS_NAME = 'pointer-events-none';
const getRowClassName = (row) => (row._isPlaceholder ? PLACEHOLDER_ROW_CLASS_NAME : undefined);

export default function PositionSizingAttributeTargetsTable({
    state,
    viewTargetKey,
    onViewTargetChange,
    rows,
    savingSettingKey,
    publishing,
    loading,
    readOnly,
    allowReadOnlySelection = false,
    onSaveSetting,
    height = 380,
}) {
    const scaffoldRows = useMemo(
        () => Array.from({ length: EMPTY_ROW_COUNT }, (_, index) => ({
            id: `attribute-target-empty-${index}`,
            _isPlaceholder: true,
        })),
        [],
    );
    const rowsForGrid = rows.length ? rows : scaffoldRows;
    const outputHeatmap = useMemo(
        () => buildPositionSizingOutputHeatmap(rows, OUTPUT_FIELDS),
        [rows],
    );
    const busy = Boolean(loading || publishing || savingSettingKey);
    const isInitialLoading = Boolean(busy && rows.length === 0);
    const columns = useMemo(() => {
        // A number column; the three settings edit in place and save through
        // onSaveSetting(updatedRow, row), showing the previous value again if
        // the save fails.
        const numeric = (field, header, size = 120) => {
            const editableField = EDITABLE_FIELDS.has(field);
            const format = (value) => formatValue(value, { field, header });
            return {
                id: field,
                header,
                size,
                accessorFn: (row) => row[field],
                sortFn: sortByNumber,
                cell: ({ row: { original } }) => {
                    if (original._isPlaceholder) return null;
                    if (!editableField) return format(original[field]);
                    return (
                        <EditableNumberCell
                            value={original[field]}
                            format={format}
                            formatInput={(value) => formatInputValue(value, { field, header })}
                            align="right"
                            editable={!readOnly && !busy}
                            aria-label={`${header} for ${original.category}`}
                            onCommit={(nextValue) => onSaveSetting({ ...original, [field]: nextValue }, original)}
                        />
                    );
                },
                meta: {
                    align: 'right',
                    cellClassName: ({ row }) => {
                        if (row.original._isPlaceholder) return undefined;
                        if (editableField) return USER_INPUT_CELL_CLASS_NAME;
                        return outputHeatmapClassName(outputHeatmap, field, row.original.id);
                    },
                },
            };
        };
        return [
            {
                id: 'category',
                header: 'Category',
                size: 170,
                accessorFn: (row) => row.category,
                sortFn: sortByText,
                cell: ({ row: { original } }) => (original._isPlaceholder ? null : original.category ?? ''),
            },
            {
                id: 'member_count',
                header: 'Count',
                size: 90,
                accessorFn: (row) => row.member_count,
                sortFn: sortByNumber,
                cell: ({ row: { original } }) => (original._isPlaceholder
                    ? null : formatValue(original.member_count, { field: 'member_count' })),
                meta: { align: 'right' },
            },
            numeric('target_capital', 'Target Capital ($)', 140),
            numeric('target_gmv', 'Target GMV ($)', 125),
            numeric('scaler', 'Scaler'),
            numeric('target_vol_pct', 'Target Vol (%)', 125),
            numeric('target_vol_dollar', 'Target Vol ($)', 125),
            numeric('pf_vol_dollar', 'Total Vol.'),
            numeric('idio_vol_dollar', 'Idio Vol.'),
            numeric('factor_vol_dollar', 'Factor Vol.'),
            numeric('idio_var_pct', 'Idio Var (%)'),
            numeric('edge_dollar', 'Edge ($)'),
            numeric('gross_beta_exp', 'Gross Beta Exp ($)', 145),
        ];
    }, [busy, onSaveSetting, outputHeatmap, readOnly]);

    return (
        <BusyOverlay show={busy && !isInitialLoading} minHeight={height + 76} spinnerSize={24}>
            <Paper
                className={cardClasses}
                elevation={1}
                sx={{ minWidth: 0, ...analyticsPanelSx, ...compactPanelPaddingSx }}
            >
                <CompactPanelHeader
                    title="Sizing Targets by Attribute"
                    description="Set risk targets for each category. Changes keep your positions and update trade ideas."
                >
                    <FormControl size="small" sx={{ minWidth: 190, ...compactSelectSx }}>
                        <InputLabel id="position-sizing-target-attribute-label">
                            Active target surface
                        </InputLabel>
                        <Select
                            labelId="position-sizing-target-attribute-label"
                            label="Active target surface"
                            value={viewTargetKey}
                            onChange={(event) => onViewTargetChange(event.target.value)}
                            disabled={busy || (readOnly && !allowReadOnlySelection)}
                        >
                            {(state?.targets || []).filter(
                                (target) => target.attribute_definition_id != null,
                            ).map((target) => {
                                const key = attributeOptionKey(
                                    target.attribute_definition_id,
                                );
                                return (
                                    <MenuItem key={key} value={key}>
                                        {target.display_name}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                </CompactPanelHeader>
                <DataTable
                    aria-label="Sizing targets by attribute"
                    data={rowsForGrid}
                    columns={columns}
                    pinnedColumns={PINNED_COLUMNS}
                    getRowClassName={getRowClassName}
                    loading={isInitialLoading}
                    height={height}
                />
            </Paper>
        </BusyOverlay>
    );
}

PositionSizingAttributeTargetsTable.propTypes = {
    state: PropTypes.object,
    viewTargetKey: PropTypes.string.isRequired,
    onViewTargetChange: PropTypes.func.isRequired,
    rows: PropTypes.array.isRequired,
    savingSettingKey: PropTypes.string,
    publishing: PropTypes.bool.isRequired,
    loading: PropTypes.bool.isRequired,
    readOnly: PropTypes.bool.isRequired,
    allowReadOnlySelection: PropTypes.bool,
    onSaveSetting: PropTypes.func.isRequired,
    height: PropTypes.number,
};
